import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAppBuilder } from "@/context/AppBuilderContext";
import { useTrackerAccess } from "@/context/TrackerAccessContext";
import { useConfirm } from "@/context/ConfirmContext";
import Notification from "@/components/NotificationBell";
import { api, absoluteUrl, formatError } from "@/api/client";
import * as PhosphorIcons from "@phosphor-icons/react";
import { toast } from "sonner";
import GlobalSearch from "@/components/GlobalSearch";
import FloatingCreate from "@/components/FloatingCreate";
import { WorkspaceBrandProvider } from "@/context/WorkspaceBrandContext";

const iconOf = (name) => PhosphorIcons[name] || PhosphorIcons.Circle;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { customize, setCustomize, canCustomize } = useAppBuilder();
  const trackerAccess = useTrackerAccess();
  const confirm = useConfirm();
  const nav = useNavigate();
  const loc = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [navItems, setNavItems] = useState([]);
  const [editing, setEditing] = useState(null); // nav item being edited
  const [showAdd, setShowAdd] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [workspace, setWorkspace] = useState({ name: "Gray Owl", tagline: "Workspace", logo_url: "", letterhead_url: "", home_image_url: "" });
  const [editingBrand, setEditingBrand] = useState(false);

  const loadNav = async () => {
    try {
      const { data } = await api.get("/nav-items");
      setNavItems(data);
    } catch {}
  };
  const loadWorkspace = async () => {
    try {
      const { data } = await api.get("/workspace/settings");
      setWorkspace(data);
    } catch {}
  };
  useEffect(() => { loadNav(); loadWorkspace(); }, []);

  if (!user) return null;

  const visibleItems = customize ? navItems : navItems.filter((i) => !i.hidden);

  const toggleHide = async (item) => {
    await api.patch(`/nav-items/${item.id}`, { hidden: !item.hidden });
    loadNav();
  };
  const rename = async (item, label) => {
    if (!label || label === item.label) { setEditing(null); return; }
    await api.patch(`/nav-items/${item.id}`, { label });
    setEditing(null); loadNav();
  };
  const remove = async (item) => {
    const ok = await confirm({ title: `Delete "${item.label}"?`, message: "This menu item will be removed from the sidebar.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    await api.delete(`/nav-items/${item.id}`);
    loadNav();
  };
  const onDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const items = [...visibleItems];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(targetIdx, 0, moved);
    setNavItems(items);
    setDragIdx(null);
    await api.post("/nav-items/reorder", items.map((i) => i.id));
    loadNav();
  };

  const openBrand = async () => { await loadWorkspace(); setEditingBrand(true); };

  return (
    <WorkspaceBrandProvider value={{ workspace, openBrandEditor: openBrand }}>
    <div className="flex min-h-screen bg-[#FAFAFA]">
      <aside className="w-64 shrink-0 border-r border-neutral-200 bg-white flex flex-col">
        <div className="px-5 py-5 border-b border-neutral-200 flex items-center justify-between group">
          <button
            type="button"
            data-testid="workspace-brand"
            onClick={() => user.role === "admin" && openBrand()}
            className="flex items-center gap-2 text-left flex-1 min-w-0"
            disabled={user.role !== "admin"}
          >
            {workspace.logo_url ? (
              <img src={absoluteUrl(workspace.logo_url)} alt="logo" className="w-8 h-8 rounded-md object-contain bg-neutral-100 border border-neutral-200" />
            ) : (
              <div className="w-8 h-8 rounded-md bg-neutral-900 flex items-center justify-center">
                <PhosphorIcons.Buildings size={18} weight="fill" className="text-white" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display font-bold text-[15px] text-neutral-900 leading-none truncate">{workspace.name || "Gray Owl"}</div>
              <div className="text-[10px] text-neutral-500 mt-1 uppercase tracking-wider truncate">{workspace.tagline || "Workspace"}</div>
            </div>
          </button>
          {user.role === "admin" && (
            <button
              type="button"
              onClick={openBrand}
              data-testid="edit-workspace-brand"
              title="Edit workspace branding"
              className="p-1 rounded hover:bg-neutral-100 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <PhosphorIcons.PencilSimple size={12} />
            </button>
          )}
          {canCustomize && (
            <button
              data-testid="customize-toggle"
              onClick={() => setCustomize(!customize)}
              title={customize ? "Exit Customize Mode" : "Customize Mode"}
              className={`ml-1 p-1.5 rounded-md transition-colors duration-200 ${customize ? "bg-amber-100 text-amber-700" : "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"}`}
            >
              <PhosphorIcons.PencilSimple size={14} weight={customize ? "fill" : "regular"} />
            </button>
          )}
        </div>

        <div className="px-3 py-3">
          <button
            data-testid="global-search-btn"
            onClick={() => setSearchOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-neutral-200 hover:bg-neutral-50 text-left text-sm text-neutral-500 transition-colors duration-200"
          >
            <PhosphorIcons.MagnifyingGlass size={14} />
            <span>Search anything…</span>
          </button>
        </div>

        {customize && (
          <div className="mx-3 mb-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex items-start gap-2">
            <PhosphorIcons.Sparkle size={12} weight="fill" className="mt-0.5 shrink-0" />
            <span>Customize mode is ON. Drag menus to reorder. Click ✏️ to rename, 👁️ to hide, 🗑️ to delete.</span>
          </div>
        )}

        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {visibleItems.map((item, idx) => {
            const Icon = iconOf(item.icon);
            if (item.admin_only && user.role !== "admin") return null;
            if (item.key === "tracker" && !customize && !trackerAccess?.hasAccess) return null;
            const isEditing = editing === item.id;
            return (
              <div
                key={item.id}
                draggable={customize}
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(idx)}
                className={`group relative flex items-center ${customize ? "cursor-move" : ""}`}
              >
                {isEditing ? (
                  <input
                    autoFocus
                    defaultValue={item.label}
                    onBlur={(e) => rename(item, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") rename(item, e.target.value); if (e.key === "Escape") setEditing(null); }}
                    className="flex-1 px-3 py-1.5 border border-blue-500 rounded-md text-sm outline-none"
                    data-testid={`nav-edit-input-${item.id}`}
                  />
                ) : (
                  <NavLink
                    to={item.path}
                    end={item.path === "/"}
                    data-testid={`nav-${item.key}`}
                    className={({ isActive }) => `dos-sidebar-item flex-1 ${isActive ? "active" : ""} ${item.hidden ? "opacity-50" : ""}`}
                    onClick={(e) => { if (customize) e.preventDefault(); }}
                  >
                    <Icon size={16} weight={loc.pathname === item.path ? "fill" : "regular"} />
                    <span>{item.label}</span>
                  </NavLink>
                )}
                {customize && !isEditing && (
                  <div className="absolute right-1 flex gap-0.5 bg-white shadow-sm border border-neutral-200 rounded-md p-0.5">
                    <button title="Rename" data-testid={`nav-rename-${item.key}`} onClick={() => setEditing(item.id)} className="p-1 rounded hover:bg-neutral-100"><PhosphorIcons.PencilSimple size={11} /></button>
                    <button title="Hide" onClick={() => toggleHide(item)} className="p-1 rounded hover:bg-neutral-100">{item.hidden ? <PhosphorIcons.EyeSlash size={11} /> : <PhosphorIcons.Eye size={11} />}</button>
                    <button title="Delete" data-testid={`nav-delete-${item.key}`} onClick={() => remove(item)} className="p-1 rounded hover:bg-red-50 text-red-500"><PhosphorIcons.Trash size={11} /></button>
                  </div>
                )}
              </div>
            );
          })}
          {customize && (
            <button data-testid="nav-add-btn" onClick={() => setShowAdd(true)} className="w-full flex items-center gap-2 px-3 py-2 mt-2 rounded-md border border-dashed border-neutral-300 text-neutral-500 hover:border-neutral-500 hover:text-neutral-900 text-sm transition-colors duration-200">
              <PhosphorIcons.Plus size={14} /> Add menu item
            </button>
          )}
        </nav>

        <div className="p-3 border-t border-neutral-200 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-[13px] font-semibold text-neutral-700">
            {user.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-neutral-900 truncate">{user.name}</div>
            <div className="text-[11px] text-neutral-500 truncate">{user.email}</div>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} className="p-1.5 rounded-md hover:bg-neutral-100 transition-colors duration-200">
            <PhosphorIcons.SignOut size={16} className="text-neutral-500" />
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-auto relative">
        <div className="fixed top-4 right-4 z-40" data-testid="notification-bell-container">
          <Notification />
        </div>
        {children}
      </main>

      <FloatingCreate />
      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
      {showAdd && <AddNavModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadNav(); }} />}
      {editingBrand && <WorkspaceBrandModal workspace={workspace} onClose={() => setEditingBrand(false)} onSaved={(w) => { setWorkspace(w); setEditingBrand(false); }} />}
    </div>
    </WorkspaceBrandProvider>
  );
}

function WorkspaceBrandModal({ workspace, onClose, onSaved }) {
  const [form, setForm] = useState({ name: workspace.name || "Gray Owl", tagline: workspace.tagline || "Workspace", logo_url: workspace.logo_url || "", letterhead_url: workspace.letterhead_url || "", home_image_url: workspace.home_image_url || "", login_image_url: workspace.login_image_url || "" });
  const [uploading, setUploading] = useState("");

  const upload = async (fieldKey, file) => {
    setUploading(fieldKey);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [fieldKey]: data.url }));
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(""); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/workspace/settings", form);
      toast.success("Workspace updated");
      onSaved(data);
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 overflow-y-auto p-4 flex items-start justify-center" onClick={onClose} data-testid="workspace-brand-modal">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-lg my-8 fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="font-display font-semibold text-neutral-900">Workspace Branding</div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><PhosphorIcons.X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">Workspace Name</label>
            <input data-testid="workspace-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="dos-input" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">Tagline</label>
            <input data-testid="workspace-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="dos-input" placeholder="Workspace" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">App Logo</label>
            <div className="text-[11px] text-neutral-500 mb-2">Shown in the sidebar, home hero, and the login screen.</div>
            <div className="flex items-center gap-3 flex-wrap">
              {form.logo_url && <img src={absoluteUrl(form.logo_url)} alt="logo" className="w-14 h-14 object-contain rounded-md border border-neutral-200 bg-neutral-50" />}
              <input data-testid="workspace-logo-file" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => e.target.files?.[0] && upload("logo_url", e.target.files[0])} className="text-xs" />
              {uploading === "logo_url" && <span className="text-xs text-neutral-500">Uploading…</span>}
              {form.logo_url && <button type="button" data-testid="workspace-logo-clear" onClick={() => setForm((f) => ({ ...f, logo_url: "" }))} className="text-xs text-red-500 hover:underline">Remove</button>}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">Global Letterhead (PNG / JPG)</label>
            <div className="text-[11px] text-neutral-500 mb-2">Used as fallback letterhead when a company doesn't have one. Applied to invoices, work orders, notes, MoM.</div>
            <div className="flex items-center gap-3">
              {form.letterhead_url && <img src={absoluteUrl(form.letterhead_url)} alt="letterhead" className="w-24 h-14 object-contain rounded-md border border-neutral-200 bg-neutral-50" />}
              <input data-testid="workspace-letterhead-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && upload("letterhead_url", e.target.files[0])} className="text-xs" />
              {uploading === "letterhead_url" && <span className="text-xs text-neutral-500">Uploading…</span>}
              {form.letterhead_url && <button type="button" data-testid="workspace-letterhead-clear" onClick={() => setForm((f) => ({ ...f, letterhead_url: "" }))} className="text-xs text-red-500 hover:underline">Remove</button>}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">Home Page Hero Image</label>
            <div className="text-[11px] text-neutral-500 mb-2">Shown at the top of the Home dashboard. Recommended 16:5 landscape.</div>
            <div className="flex items-center gap-3 flex-wrap">
              {form.home_image_url && <img src={absoluteUrl(form.home_image_url)} alt="home hero" className="w-32 h-16 object-cover rounded-md border border-neutral-200 bg-neutral-50" />}
              <input data-testid="workspace-home-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && upload("home_image_url", e.target.files[0])} className="text-xs" />
              {uploading === "home_image_url" && <span className="text-xs text-neutral-500">Uploading…</span>}
              {form.home_image_url && <button type="button" data-testid="workspace-home-clear" onClick={() => setForm((f) => ({ ...f, home_image_url: "" }))} className="text-xs text-red-500 hover:underline">Remove</button>}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">Login Page Hero Image</label>
            <div className="text-[11px] text-neutral-500 mb-2">Shown on the right half of the sign-in screen. Recommended 3:4 portrait or square.</div>
            <div className="flex items-center gap-3 flex-wrap">
              {form.login_image_url && <img src={absoluteUrl(form.login_image_url)} alt="login hero" className="w-24 h-24 object-cover rounded-md border border-neutral-200 bg-neutral-50" />}
              <input data-testid="workspace-login-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && upload("login_image_url", e.target.files[0])} className="text-xs" />
              {uploading === "login_image_url" && <span className="text-xs text-neutral-500">Uploading…</span>}
              {form.login_image_url && <button type="button" data-testid="workspace-login-clear" onClick={() => setForm((f) => ({ ...f, login_image_url: "" }))} className="text-xs text-red-500 hover:underline">Remove</button>}
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button data-testid="workspace-save" type="submit" className="dos-btn dos-btn-primary">Save</button>
        </div>
      </form>
    </div>
  );
}

function AddNavModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ label: "", path: "/", icon: "Circle" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/nav-items", { ...form, order: 100 });
      toast.success("Menu added"); onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 overflow-y-auto p-4 flex items-start justify-center" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-sm my-8 fade-in">
        <div className="px-5 py-4 border-b border-neutral-200 font-display font-semibold">Add Menu Item</div>
        <div className="p-5 space-y-3">
          <div><label className="text-xs text-neutral-500">Label</label><input data-testid="add-nav-label" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="dos-input mt-1" /></div>
          <div><label className="text-xs text-neutral-500">Path</label><input required value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} className="dos-input mt-1" placeholder="/my-page" /></div>
          <div><label className="text-xs text-neutral-500">Icon Name (Phosphor)</label><input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="dos-input mt-1" placeholder="Circle, Star, Hash, etc." /></div>
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button data-testid="add-nav-save" type="submit" className="dos-btn dos-btn-primary">Add</button>
        </div>
      </form>
    </div>
  );
}
